// Inverio Brand Colors
// Apple-inspired theme with brand teal accent
// Traffic light system for status indicators
// Supports both light and dark modes

// Light theme colors
const lightColors = {
  // Primary Brand - Teal from logo
  primary: {
    50: '#E6FCFC',
    100: '#B3F5F5',
    200: '#80EEEE',
    300: '#4DE7E7',
    400: '#26E0E0',
    500: '#00D9D9', // Main brand teal
    600: '#00B8B8',
    700: '#009999',
    800: '#007A7A',
    900: '#005C5C',
  },

  // Light backgrounds (Apple Mac style)
  light: {
    50: '#FFFFFF',   // Pure white
    100: '#FAFAFA',  // Off-white
    200: '#F5F5F7',  // Apple grey
    300: '#E8E8ED',  // Light grey
    400: '#D2D2D7',  // Medium grey
    500: '#AEAEB2',  // Grey
    600: '#8E8E93',  // Dark grey
    700: '#636366',  // Darker grey
    800: '#48484A',  // Very dark grey
    900: '#1D1D1F',  // Almost black (text)
  },

  // Traffic Light Status System
  // Green = Positive/Good/Success
  traffic: {
    green: '#34C759',      // iOS green
    greenLight: '#E8F9ED',
    greenMuted: '#A8E6B4',
    // Amber = Caution/Medium/Warning (alias)
    amber: '#FF9500',
    amberLight: '#FFF4E6',
    // Red = Alert/Critical/Error (alias)
    red: '#FF3B30',
    redLight: '#FFEBEA',
  },
  // Yellow/Amber = Caution/Medium/Warning
  amber: {
    main: '#FF9500',       // iOS orange/amber
    light: '#FFF4E6',
    muted: '#FFD699',
  },
  // Red = Alert/Critical/Error
  red: {
    main: '#FF3B30',       // iOS red
    light: '#FFEBEA',
    muted: '#FF9E99',
  },

  // Semantic aliases using traffic light system
  success: '#34C759',      // Traffic green
  warning: '#FF9500',      // Traffic amber
  error: '#FF3B30',        // Traffic red
  info: '#00D9D9',         // Brand teal

  // Card and surface colors
  surface: {
    primary: '#FFFFFF',
    secondary: '#F5F5F7',
    tertiary: '#E8E8ED',
    elevated: '#FFFFFF',
  },

  // Text colors
  text: {
    primary: '#1D1D1F',
    secondary: '#636366',
    tertiary: '#8E8E93',
    inverse: '#FFFFFF',
    brand: '#00B8B8',
  },

  // Border colors
  border: {
    light: '#E8E8ED',
    medium: '#D2D2D7',
    dark: '#AEAEB2',
  },

  // Legacy support - dark theme values (for gradients or specific components)
  dark: {
    50: '#F5F5F7',
    100: '#E8E8ED',
    200: '#D2D2D7',
    300: '#AEAEB2',
    400: '#8E8E93',
    500: '#636366',
    600: '#48484A',
    700: '#3A3A3C',
    800: '#2C2C2E',
    900: '#1D1D1F',
    950: '#000000',
  },

  // Secondary accent (minimal use)
  secondary: {
    50: '#FFF7E6',
    100: '#FFE9B3',
    200: '#FFDB80',
    300: '#FFCD4D',
    400: '#FFC226',
    500: '#FF9500', // Amber for secondary actions
    600: '#CC7700',
    700: '#995900',
    800: '#663C00',
    900: '#331E00',
  },

  // Gradients
  gradients: {
    primary: ['#00D9D9', '#00B8B8'],
    primarySoft: ['#E6FCFC', '#B3F5F5'],
    light: ['#FFFFFF', '#F5F5F7'],
    surface: ['#FFFFFF', '#FAFAFA'],
  },
};

// Dark theme colors
const darkColors = {
  // Primary Brand - Teal from logo (brighter for dark mode)
  primary: {
    50: '#0A2E2E',
    100: '#0D3D3D',
    200: '#115555',
    300: '#157070',
    400: '#1A8A8A',
    500: '#00D9D9', // Main brand teal
    600: '#00E5E5',
    700: '#33EBEB',
    800: '#66F0F0',
    900: '#99F5F5',
  },

  // Dark backgrounds
  light: {
    50: '#000000',
    100: '#0A0A0A',
    200: '#121212',
    300: '#1C1C1E',
    400: '#2C2C2E',
    500: '#3A3A3C',
    600: '#48484A',
    700: '#636366',
    800: '#8E8E93',
    900: '#F5F5F7',
  },

  // Traffic Light Status System (same, good contrast on dark)
  traffic: {
    green: '#32D74B',
    greenLight: '#1A3D22',
    greenMuted: '#2D5A36',
    // Amber = Caution/Medium/Warning (alias)
    amber: '#FF9F0A',
    amberLight: '#3D2E0A',
    // Red = Alert/Critical/Error (alias)
    red: '#FF453A',
    redLight: '#3D1512',
  },
  amber: {
    main: '#FF9F0A',
    light: '#3D2E0A',
    muted: '#5A4510',
  },
  red: {
    main: '#FF453A',
    light: '#3D1512',
    muted: '#5A201A',
  },

  // Semantic aliases
  success: '#32D74B',
  warning: '#FF9F0A',
  error: '#FF453A',
  info: '#00D9D9',

  // Card and surface colors (dark)
  surface: {
    primary: '#1C1C1E',
    secondary: '#000000',
    tertiary: '#2C2C2E',
    elevated: '#2C2C2E',
  },

  // Text colors (inverted for dark)
  text: {
    primary: '#F5F5F7',
    secondary: '#AEAEB2',
    tertiary: '#8E8E93',
    inverse: '#1D1D1F',
    brand: '#00E5E5',
  },

  // Border colors (dark)
  border: {
    light: '#2C2C2E',
    medium: '#3A3A3C',
    dark: '#48484A',
  },

  // Legacy support
  dark: {
    50: '#000000',
    100: '#0A0A0A',
    200: '#121212',
    300: '#1C1C1E',
    400: '#2C2C2E',
    500: '#3A3A3C',
    600: '#48484A',
    700: '#636366',
    800: '#8E8E93',
    900: '#F5F5F7',
    950: '#FFFFFF',
  },

  // Secondary accent
  secondary: {
    50: '#331E00',
    100: '#4D2D00',
    200: '#663C00',
    300: '#995900',
    400: '#CC7700',
    500: '#FF9F0A',
    600: '#FFB340',
    700: '#FFC766',
    800: '#FFDB99',
    900: '#FFEFCC',
  },

  // Gradients (dark mode)
  gradients: {
    primary: ['#00B8B8', '#00D9D9'],
    primarySoft: ['#0A2E2E', '#0D3D3D'],
    light: ['#1C1C1E', '#000000'],
    surface: ['#1C1C1E', '#121212'],
  },
};

// Export both for direct access if needed
export { lightColors, darkColors };

// Default export is light colors for backwards compatibility
export const colors = lightColors;

// Traffic light score color system
export const getScoreColor = (score: number): string => {
  if (score >= 70) return colors.traffic.green;   // Good
  if (score >= 40) return colors.amber.main;       // Caution
  return colors.red.main;                          // Alert
};

// Traffic light background for scores
export const getScoreBgColor = (score: number): string => {
  if (score >= 70) return colors.traffic.greenLight;
  if (score >= 40) return colors.amber.light;
  return colors.red.light;
};

// Category colors (using brand teal variations)
export const categoryColors: Record<string, string> = {
  team: colors.primary[600],
  market: colors.primary[500],
  product: colors.primary[700],
  traction: colors.primary[400],
  financials: colors.primary[800],
};
